// Client-side file validation mirroring the backend's file-storage validator.
// Keep these in sync with the API (file-storage.service.ts). Overridable server-side
// via MAX_FILE_SIZE / ALLOWED_FILE_TYPES, but these are the live defaults.

// Investigation documents: passport photo, government ID, signed affidavit,
// letter of employment, and any additional document.
export const MAX_INVESTIGATION_FILE_SIZE = 10 * 1024 * 1024; // 10 MB per file

export const ALLOWED_INVESTIGATION_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
] as const;

// User-facing list of extensions for `accept` attributes and error copy.
export const ALLOWED_INVESTIGATION_EXTENSIONS = '.jpg,.jpeg,.png,.pdf,.doc,.docx';

// Training videos take a different path on the backend: any video/*, up to the
// 200 MB global transport ceiling.
export const MAX_VIDEO_FILE_SIZE = 200 * 1024 * 1024; // 200 MB

function formatMb(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return Number.isInteger(mb) ? `${mb}MB` : `${mb.toFixed(1)}MB`;
}

/**
 * Validate an investigation document. Returns an error message string if invalid,
 * or null if the file passes. Messages mirror the API responses so they read the
 * same whether caught client-side or returned by the server.
 */
export function validateInvestigationFile(file: File): string | null {
    if (file.size > MAX_INVESTIGATION_FILE_SIZE) {
        return `File size exceeds maximum allowed size of ${formatMb(MAX_INVESTIGATION_FILE_SIZE)}`;
    }
    // iPhone HEIC (image/heic / image/heif) and other unlisted types are rejected
    // by the backend — block them here with clear guidance.
    if (!ALLOWED_INVESTIGATION_MIME_TYPES.includes(file.type as (typeof ALLOWED_INVESTIGATION_MIME_TYPES)[number])) {
        const shown = file.type || 'this file type';
        return `File type ${shown} is not allowed. Allowed types: JPG, PNG, PDF, DOC, DOCX (iPhone HEIC photos must be converted to JPG or PNG).`;
    }
    return null;
}

// Document-only subset: PDF or Word, no images. Used where an image makes no
// sense (e.g. a signed affidavit must be a real document, not a photo).
export const ALLOWED_DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
] as const;

/**
 * Validate a document-only upload: PDF or Word (.doc/.docx), ≤10 MB — images rejected.
 */
export function validateDocumentOnlyFile(file: File): string | null {
    if (file.size > MAX_INVESTIGATION_FILE_SIZE) {
        return `File size exceeds maximum allowed size of ${formatMb(MAX_INVESTIGATION_FILE_SIZE)}`;
    }
    if (!ALLOWED_DOCUMENT_MIME_TYPES.includes(file.type as (typeof ALLOWED_DOCUMENT_MIME_TYPES)[number])) {
        return 'Please upload a PDF or Word document (.pdf, .doc, .docx). Images are not accepted here.';
    }
    return null;
}

/**
 * Validate a training video: must be a video/* type and within the 200 MB ceiling.
 */
export function validateVideoFile(file: File): string | null {
    if (!file.type.startsWith('video/')) {
        return 'File type is not allowed. Please upload a video file.';
    }
    if (file.size > MAX_VIDEO_FILE_SIZE) {
        return `File size exceeds maximum allowed size of ${formatMb(MAX_VIDEO_FILE_SIZE)}`;
    }
    return null;
}
